// useSamplingEventAutosave.js
// Hook for Sampling Event autosave with websocket and IndexedDB queue fallback

import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeSamplingEventSocket,
  joinSamplingEvent,
  leaveSamplingEvent,
  emitSamplingEventPatch,
  onSamplingEventConnectionChange,
  onSamplingEventUpdate,
  onSamplingEventJsaUpdate,
  clearSamplingEventHandlers,
} from "../services/samplingEventSocketService";
import {
  queuePatch,
  flushQueue,
  getPendingCount,
} from "../services/samplingEventPatchQueue";
import { useAppStore } from "../store/appStore";

export const useSamplingEventAutosave = (eventId) => {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [roomStatus, setRoomStatus] = useState("idle"); // idle | joining | joined | error
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, saved, queued, error
  const [pendingCount, setPendingCount] = useState(0);
  const joinedRef = useRef(false);
  const eventIdRef = useRef(eventId);

  // Keep eventId ref current
  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);

  // Initialize socket, join room, and set up handlers
  useEffect(() => {
    if (!eventId) return;

    initializeSamplingEventSocket();
    setRoomStatus("joining");

    const handleConnectionChange = async (connected) => {
      setConnectionStatus(connected ? "connected" : "disconnected");
      if (!connected) {
        // Reset joined flag on disconnect so we re-join on reconnect
        joinedRef.current = false;
        setRoomStatus("joining");
      }
      if (connected && eventIdRef.current) {
        try {
          if (!joinedRef.current) {
            setRoomStatus("joining");
            const ack = await joinSamplingEvent(eventIdRef.current);
            joinedRef.current = Boolean(ack?.success);
            setRoomStatus(ack?.success ? "joined" : "error");
          }
          const flushed = await flushQueue(
            eventIdRef.current,
            emitSamplingEventPatch
          );
          if (flushed > 0) {
            const remaining = await getPendingCount(eventIdRef.current);
            setPendingCount(remaining);
          }
        } catch (err) {
          console.error("Error during Sampling Event socket reconnection:", err);
        }
      }
    };

    // Handle incoming broadcasts - route to store's applyWebsocketPatch
    const handleUpdate = (patchData) => {
      try {
        useAppStore
          .getState()
          .activeSamplingEventActions.applyWebsocketPatch(patchData);
      } catch (e) {
        console.error("Failed to apply websocket patch:", e);
      }
    };

    // Handle incoming JSA broadcasts - route to JSA-specific handler
    const handleJsaUpdate = (patchData) => {
      try {
        useAppStore
          .getState()
          .activeSamplingEventActions.applyJsaWebsocketPatch(patchData);
      } catch (e) {
        console.error("Failed to apply JSA websocket patch:", e);
      }
    };

    onSamplingEventConnectionChange(handleConnectionChange);
    onSamplingEventUpdate(handleUpdate);
    onSamplingEventJsaUpdate(handleJsaUpdate);

    // Initial join
    joinSamplingEvent(eventId)
      .then((ack) => {
        joinedRef.current = Boolean(ack?.success);
        setRoomStatus(ack?.success ? "joined" : "error");
      })
      .catch((err) => console.error("Failed to join Sampling Event room:", err));

    // Update pending count
    getPendingCount(eventId)
      .then(setPendingCount)
      .catch((err) => console.error("Failed to get pending patch count:", err));

    return () => {
      if (eventIdRef.current && joinedRef.current) {
        leaveSamplingEvent(eventIdRef.current).catch((err) =>
          console.warn("Failed to leave Sampling Event room:", err)
        );
        joinedRef.current = false;
      }
      clearSamplingEventHandlers();
    };
  }, [eventId]);

  // Emit patch with queue fallback
  const emitPatch = useCallback(async (patch) => {
    if (!eventIdRef.current) return false;

    setSaveStatus("saving");

    const result = await emitSamplingEventPatch(eventIdRef.current, patch);

    if (!result.error && !result.shouldQueue) {
      // Success
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
      return true;
    }

    if (result.shouldQueue) {
      // Network issue (disconnected, timeout, no response) - queue for retry
      try {
        await queuePatch(eventIdRef.current, patch);
        const count = await getPendingCount(eventIdRef.current);
        setPendingCount(count);
      } catch (idbErr) {
        console.warn(
          "Failed to queue Sampling Event patch to browser storage. Your changes may not be saved offline. Try refreshing the page if this persists.",
          idbErr?.message || idbErr
        );
      }
      setSaveStatus("queued");
    } else {
      // Server validation error - show to user, DON'T queue
      setSaveStatus("error");
      console.error("Sampling event patch validation error:", result.error);
      // If unauthorized, redirect to login
      if (result.error === "Unauthorized") {
        window.location.href = "/login";
      }
      // Reset to idle after showing error briefly
      setTimeout(() => setSaveStatus("idle"), 3000);
    }

    return false;
  }, []);

  return {
    emitPatch,
    connectionStatus,
    roomStatus,
    saveStatus,
    pendingCount,
    isConnected: connectionStatus === "connected",
  };
};
